import { type FunctionComponent, type ReactNode, useState } from 'react';

import type { AsyncDataReloaderT } from '@dr.pogodin/react-global-state';
import { Link, WithTooltip, time } from '@dr.pogodin/react-utils';

import Status from 'components/Status';
import Throbber from 'components/Throbber';

import VideoThumbnail from 'components/VideoThumbnail';
import { TransformationStages } from 'components/admin';
import AdminPractitionerLink from 'components/admin/PractitionerLink';
import RemoveVideoModal from 'components/admin/RemoveVideoModal';
import { useArtist } from 'hooks';

import {
  MDASH,
  type VideoListT,
  type VideoT,
  formatMinsAsMMSS,
  getId,
  useGlobalState,
  useGlobalStateObject,
} from 'utils';

import IconEdit from 'assets/images/icon-edit-16px.svg';

import IconTrash from 'assets/images/icon-trash-16px.svg';

import VideoPageLink from './VideoPageLink';

import videoThumbnailTheme from './video-thumbnail.scss';

import './style.scss';

type PropsT = {
  reloadVideos: AsyncDataReloaderT<VideoListT>;
  video: VideoT;
};

const VideoRow: FunctionComponent<PropsT> = ({ reloadVideos, video }) => {
  const [perms] = useGlobalState('user.permissions');
  if (!perms) throw Error('Unauthorized');

  const artist = useArtist(video.artistId);

  let createdBy: ReactNode;
  if (perms.EDIT_ANY_VIDEO_DETAILS) {
    if (artist.data) {
      createdBy = <td data-label="Created By"><AdminPractitionerLink practitioner={artist.data} /></td>;
    } else if (artist.loading) createdBy = <td data-label="Created By"><Throbber /></td>;
    else createdBy = <td data-label="Created By">{MDASH}</td>;
  }

  const gs = useGlobalStateObject();

  let warning: ReactNode;
  if (video.status === 'Published') {
    if (artist.data && artist.data.status !== 'Active') {
      warning = 'Practioner is not active';
    }
    if (!video.isReadyToBePlayed) {
      warning = <>{warning} Video is still being processed</>;
    }

    if (warning) {
      warning = <>Video is not yet visible to end users. {warning}</>;
    }
  }

  const [showRemoveModal, setShowRemoveModal] = useState(false);

  const videoId = getId(video);

  return (
    <tr styleName="row">
      <td>
        <VideoPageLink video={video}>
          <VideoThumbnail
            imageUrl={video.thumbnailUrl}
            noPlayIcon
            theme={videoThumbnailTheme}
          />
        </VideoPageLink>
      </td>
      <td aria-label="title-and-stages">
        <div styleName="titleAndStages">
          <div>{video.title ?? 'Untitled'}</div>
          <TransformationStages stages={video.transformationStages} />
        </div>
      </td>
      {createdBy}
      <td data-label="Created At">
        {video.createdAt ? time(video.createdAt).format('MMM D, YYYY') : MDASH}
      </td>
      <td data-label="Modalities">
        {video.modalities.length ? video.modalities.join(', ') : MDASH}
      </td>
      <td data-label="Duration">
        {typeof video.durationMin === 'number'
          ? `${formatMinsAsMMSS(video.durationMin)} mins`
          : MDASH}
      </td>
      <td data-label="Published Date">
        {video.publishedAt
          ? time(video.publishedAt).format('MMMM D, YYYY')
          : MDASH}
      </td>
      <td data-label="Status"><Status status={video.status} warning={warning} /></td>
      <td aria-label="Actions">
        <div styleName="icons">
          <WithTooltip tip="Edit Video">
            <VideoPageLink video={video}><IconEdit /></VideoPageLink>
          </WithTooltip>
          <WithTooltip tip="Delete Video">
            <Link
              onClick={() => {
                setShowRemoveModal(true);
              }}
              to=""
            >
              <IconTrash />
            </Link>
          </WithTooltip>
        </div>
        {showRemoveModal ? (
          <RemoveVideoModal
            onClose={(removed) => {
              if (removed) {
                const gsv = gs.get('videos.data.items');
                if (gsv) {
                  const idx = gsv.findIndex((item) => item.id === videoId);
                  if (idx >= 0) {
                    gs.set('videos.data.items', [
                      ...gsv.slice(0, idx),
                      ...gsv.slice(idx + 1),
                    ]);
                  }
                }

                void reloadVideos();
              }

              setShowRemoveModal(false);
            }}
            videoId={videoId}
          />
        ) : null}
      </td>
    </tr>
  );
};

export default VideoRow;
